"use client";

import { useEffect, useId, useRef, useState } from "react";
import CustomImage from "../../atoms/customImage";
import PropTypes from "prop-types";
import Typography from "../../atoms/typography";
import { formatClassNames, getIconUrl, updateCurrency } from "../../../utils/common";
import { CaretDownIcon, CaretUpIcon, EyeIcon, XIcon } from "@phosphor-icons/react";
import { PERSONALIZED_DETAILS_CONSTANT } from "../../../constants/common/common";

const imageDomain = process.env.NEXT_PUBLIC_ASSET_PREFIX;
const MAX_ITEM_COUNT = 9;

/**
 * ItemCard component represents a single item card in the cart.
 * @param {Object} props - The props object.
 * @param {Object} props.item - The item object.
 * @returns {JSX.Element} The ItemCard component.
 */
const ItemCard = ({
  item,
  isMobile,
  selectedCurrency,
  isAddon = false,
  onQuantityClick,
  onQuantitySelect,
  quantityOverride,
  isShowTrashIcon = false,
  onTrashIconClick = () => {},
  onPersonalizationViewClick = () => {},
  isOutOfStock = false,
  isFromCheckout = false,
  hasComboProduct = false,
  isFromDrawer = false,
}) => {
  const [isQuantityDropdownOpen, setIsQuantityDropdownOpen] = useState(false);
  const quantityDropdownRef = useRef(null);
  const quantityListboxId = useId();
  const imageSize = hasComboProduct ? 48 : isAddon ? 64 : 80;
  const product = item?.mainproduct || item;
  const { productName, productImage, price, quantity } = product;
  const displayQuantity = quantityOverride ?? quantity;
  const quantityOptions = Array.from({ length: MAX_ITEM_COUNT }, (_, index) => index + 1);
  /**
   * Closes quantity dropdown when clicking outside on desktop.
   *
   * @param {MouseEvent} event
   */
  const onCloseQuantityDropdownOutside = (event) => {
    if (quantityDropdownRef.current && !quantityDropdownRef.current.contains(event.target)) {
      setIsQuantityDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (!isQuantityDropdownOpen || isMobile) {
      return undefined;
    }

    document.addEventListener("mousedown", onCloseQuantityDropdownOutside);
    return () => document.removeEventListener("mousedown", onCloseQuantityDropdownOutside);
  }, [isQuantityDropdownOpen, isMobile]);

  /**
   * Opens or closes the desktop quantity dropdown.
   * Fires quantity stepper click analytics only when opening.
   */
  const onToggleQuantityDropdown = () => {
    if (!isQuantityDropdownOpen) {
      onQuantityClick?.();
    }
    setIsQuantityDropdownOpen((prev) => !prev);
  };

  /**
   * Applies selected quantity on desktop dropdown pick.
   *
   * @param {number} selectedQuantity
   * @param {React.MouseEvent} event
   */
  const onSelectQuantity = (selectedQuantity, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setIsQuantityDropdownOpen(false);
    onQuantitySelect?.(selectedQuantity);
  };

  /**
   * Handles image load error with fallback asset.
   * @param {Event} event
   */
  const onOccasionImageError = (event) => {
    event.target.onerror = null;
    event.target.src = getIconUrl("/icons/placeholder-bb.png");
  };

  /**
   *
   * @returns {void}
   */
  const onClickQuantity = () => (!isOutOfStock ? onQuantityClick() : null);
  /**
   * @returns {void}
   */
  const onClickToggleQuantityDropdown = () => (!isOutOfStock ? onToggleQuantityDropdown() : null);

  /**
   * @returns {JSX.Element} The product image element.
   * @param {string} productImage - The product image.
   * @param {string} productName - The product name.
   * @param {number} imageSize - The image size.
   * @param {boolean} isAddon - Whether the item is an addon.
   * @param {boolean} isOutOfStock - Whether the item is out of stock.
   * @param {function} onOccasionImageError - The function to handle image error.
   */
  const productImageElement = (
    <CustomImage
      src={imageDomain + productImage}
      alt={productName}
      width={imageSize}
      height={imageSize}
      sizes={`${imageSize}px`}
      isPriority={false}
      borderRadius={8}
      className={formatClassNames(
        isAddon ? "max-h-[64px] min-w-[64px] border-1 border-thinLightGrey" : "max-h-[80px] min-w-[80px]",
        hasComboProduct && "[&&]:max-h-[48px] [&&]:min-w-[48px]",
      )}
      onError={onOccasionImageError}
    />
  );

  const quantityControl = onQuantityClick ? (
    <div className={formatClassNames("flex items-center", isMobile ? "justify-between" : "justify-start gap-24")}>
      {isMobile ? (
        <button
          type="button"
          className={formatClassNames(
            "flex w-fit items-center gap-4 pb-4 text-12 font-500 leading-16 text-grey-400",
            isAddon && "[&&]:pb-0",
          )}
          onClick={onClickQuantity}
          aria-label={`Update quantity for ${productName}, currently ${displayQuantity}`}
        >
          Qty {displayQuantity}
          {!isOutOfStock && <CaretDownIcon size={12} weight="bold" aria-hidden="true" />}
        </button>
      ) : (
        <div ref={quantityDropdownRef} className={formatClassNames("relative w-max", isQuantityDropdownOpen && "z-50")}>
          <button
            type="button"
            className={formatClassNames(
              "flex w-fit items-center gap-4 pb-4 text-12 font-500 leading-16 text-grey-400",
              isAddon && "[&&]:pb-0",
            )}
            onClick={onClickToggleQuantityDropdown}
            aria-label={`Update quantity for ${productName}, currently ${displayQuantity}`}
            aria-haspopup="listbox"
            aria-expanded={isQuantityDropdownOpen}
            aria-controls={quantityListboxId}
          >
            Qty {displayQuantity}
            {isQuantityDropdownOpen && !isOutOfStock ? (
              <CaretUpIcon size={12} weight="bold" aria-hidden="true" />
            ) : !isOutOfStock ? (
              <CaretDownIcon size={12} weight="bold" aria-hidden="true" />
            ) : null}
          </button>

          {isQuantityDropdownOpen && (
            <ul
              id={quantityListboxId}
              role="listbox"
              aria-label={`Select quantity for ${productName}`}
              className="absolute left-0 top-full z-50 mt-4 max-h-[160px] min-w-[72px] overflow-y-auto rounded-8 border-1 border-thinLightGrey bg-white-900 py-4 shadow-[0_4px_16px_0_#0000001F] scrollbar-thin"
            >
              {quantityOptions.map((qty) => (
                <li key={qty} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={Number(displayQuantity) === qty}
                    className={formatClassNames(
                      "flex w-full items-center justify-center px-16 py-8 text-14 font-500 leading-20 text-fnp-500",
                      Number(displayQuantity) === qty && "bg-grey-50",
                    )}
                    onMouseDown={(event) => onSelectQuantity(qty, event)}
                  >
                    {qty}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!isAddon && product?.isProductPersonalizable && (
        <button
          type="button"
          className={formatClassNames(
            isMobile ? "-mr-30" : "mr-0",
            "flex items-center gap-6 rounded-8 border-1 border-thinLightGrey px-8 py-6 text-12 font-500 leading-16 text-fnp-300",
          )}
          onClick={onPersonalizationViewClick}
          aria-label={`Personalisation view for ${productName}`}
        >
          <EyeIcon size={16} />
          {PERSONALIZED_DETAILS_CONSTANT.PERSONALIZATION}
        </button>
      )}
    </div>
  ) : (
    <Typography variant="p" Tag="p" classes="font-500 text-12 text-grey-400 leading-[16px]">
      Qty {displayQuantity}
    </Typography>
  );

  return (
    <div className={formatClassNames("flex w-full justify-between", isAddon && "pl-18")}>
      <div
        className={formatClassNames(
          "flex items-center gap-10",
          !isFromDrawer ? "max-w-[calc(80%-60px)]" : "max-w-[calc(80%-16px)]",
        )}
      >
        {isFromCheckout ? (
          <a className="flex w-full" href={item?.productUrl}>
            {productImageElement}
          </a>
        ) : (
          productImageElement
        )}

        <div
          className={`flex w-full flex-col justify-between ${hasComboProduct ? "[&&]:min-h-[48px]" : isAddon ? "[&&]:min-h-[64px]" : "[&&]:min-h-[80px]"}`}
        >
          <div className="flex flex-col gap-[4px]">
            <Typography variant="p" Tag="p" classes="font-400 text-14 text-fnp-500 leading-[20px] truncate">
              {productName}
            </Typography>

            <Typography variant="p" Tag="p" classes="font-600 text-14 text-black-900 leading-[20px]">
              {updateCurrency(selectedCurrency, price)}
            </Typography>
          </div>

          {!hasComboProduct && !isFromDrawer ? quantityControl : null}
        </div>
      </div>
      {isShowTrashIcon && (
        <button
          type="button"
          className="mb-auto shrink-0 border-0 bg-transparent p-0"
          onClick={onTrashIconClick}
          aria-label={`Remove ${productName} from cart`}
        >
          <XIcon size={16} color="#757575" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

ItemCard.propTypes = {
  item: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedCurrency: PropTypes.object.isRequired,
  isAddon: PropTypes.bool,
  onQuantityClick: PropTypes.func,
  onQuantitySelect: PropTypes.func,
  quantityOverride: PropTypes.number,
  isShowTrashIcon: PropTypes.bool,
  onTrashIconClick: PropTypes.func,
  onPersonalizationViewClick: PropTypes.func,
  isOutOfStock: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
  hasComboProduct: PropTypes.bool,
  isFromDrawer: PropTypes.bool,
};

export default ItemCard;
